import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button/Button';
import { Input } from '../ui/Input/Input';
import { collaborationAPI } from '../../api/collaboration';
import { CollaborationPanel } from './CollaborationPanel';
import { CopyIcon } from '../Icons/Icons';
import type { 
  InvitationRequest, 
  SharingSettings,
  CollaborationRole 
} from '../../types/collaboration';
import './ShareModal.css';

interface ShareModalProps {
  examinationId: string;
  examinationTitle: string;
  isShared: boolean;
  sharingSettings?: SharingSettings;
  currentUserRole: CollaborationRole;
  onClose: () => void;
  onSharingToggle: (enabled: boolean) => void;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  examinationId,
  examinationTitle,
  isShared,
  sharingSettings,
  currentUserRole,
  onClose,
  onSharingToggle,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'settings' | 'collaborators' | 'activity'>('settings');
  const [inviteToken, setInviteToken] = useState<string>('');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState<InvitationRequest>({
    email: '',
    role: 'viewer',
  });
  const [inviting, setInviting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copying, setCopying] = useState(false);

  // 共有設定の変更
  const handleSharingToggle = async () => {
    try {
      if (isShared) {
        await collaborationAPI.disableSharing(examinationId);
      } else {
        await collaborationAPI.enableSharing(examinationId);
      }
      onSharingToggle(!isShared);
    } catch (error) {
      alert(t('share.error.settingsFailed'));
    }
  };

  // 招待リンクの生成
  const handleGenerateInviteLink = async () => {
    setGenerating(true);
    try {
      const result = await collaborationAPI.generateInviteLink(examinationId, 'viewer');
      setInviteToken(result.token);
      alert(t('share.success.linkGenerated'));
    } catch (error) {
      alert(error instanceof Error ? error.message : t('share.error.linkGenerationFailed'));
    } finally {
      setGenerating(false);
    }
  };

  // 招待リンクのコピー
  const handleCopyInviteLink = async () => {
    if (!inviteToken) return;
    
    setCopying(true);
    try {
      const inviteUrl = `${window.location.origin}/invite/${inviteToken}`;
      await navigator.clipboard.writeText(inviteUrl);
      alert(t('share.success.linkCopied'));
    } catch (error) {
      alert(t('share.error.copyFailed'));
    } finally {
      setCopying(false);
    }
  };

  // 招待の送信
  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteForm.email.trim()) return;

    setInviting(true);
    try {
      const result = await collaborationAPI.inviteCollaborator(examinationId, inviteForm);
      setInviteToken(result.token);
      setInviteForm({ email: '', role: 'viewer' });
      setShowInviteForm(false);
      alert(t('share.success.invitationSent'));
    } catch (error) {
      alert(error instanceof Error ? error.message : t('share.error.invitationFailed'));
    } finally {
      setInviting(false);
    }
  };

  const canInviteUsers = currentUserRole === 'owner' || 
    (currentUserRole === 'collaborator' && 
     sharingSettings?.allowInvites !== false);

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal__header">
          <h2 className="share-modal__title">
            「{examinationTitle}」の共有設定
          </h2>
          <button className="share-modal__close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="share-modal__tabs">
          <button
            className={`tab ${activeTab === 'settings' ? 'tab--active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            設定
          </button>
          <button
            className={`tab ${activeTab === 'collaborators' ? 'tab--active' : ''}`}
            onClick={() => setActiveTab('collaborators')}
          >
            共同作業者
          </button>
          <button
            className={`tab ${activeTab === 'activity' ? 'tab--active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            活動ログ
          </button>
        </div>

        <div className="share-modal__content">
          {activeTab === 'settings' && (
            <div className="share-settings">
              <div className="setting-item">
                <div className="setting-item__info">
                  <h3 className="setting-item__title">共有を有効にする</h3>
                  <p className="setting-item__description">
                    他のユーザーと評価プロジェクトを共同で作業できるようになります
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={isShared}
                    onChange={handleSharingToggle}
                    disabled={currentUserRole !== 'owner'}
                  />
                  <span className="toggle-switch__slider"></span>
                </label>
              </div>

              {isShared && (
                <>
                  <div className="setting-item">
                    <div className="setting-item__info">
                      <h3 className="setting-item__title">招待リンク</h3>
                      <p className="setting-item__description">
                        このリンクを共有して他のユーザーを招待できます
                      </p>
                    </div>
                    {inviteToken ? (
                      <div className="invite-link-section">
                        <div className="invite-link">
                          {`${window.location.origin}/invite/${inviteToken}`}
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleCopyInviteLink}
                          disabled={copying}
                          className="copy-button"
                        >
                          <CopyIcon />
                          <span>{copying ? 'コピー中...' : 'コピー'}</span>
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="secondary"
                        onClick={handleGenerateInviteLink}
                        disabled={!canInviteUsers || generating}
                      >
                        {generating ? '生成中...' : '招待リンクを生成'}
                      </Button>
                    )}
                  </div>

                  {canInviteUsers && (
                    <div className="setting-item">
                      <div className="setting-item__info">
                        <h3 className="setting-item__title">メールで招待</h3>
                        <p className="setting-item__description">
                          メールアドレスを指定して直接招待を送信
                        </p>
                      </div>
                      <Button
                        variant="primary"
                        onClick={() => setShowInviteForm(true)}
                        disabled={showInviteForm}
                      >
                        メール招待
                      </Button>
                    </div>
                  )}

                  {showInviteForm && (
                    <div className="invite-form-section">
                      <form className="invite-form" onSubmit={handleSendInvite}>
                        <div className="form-row">
                          <Input
                            label="メールアドレス"
                            type="email"
                            value={inviteForm.email}
                            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                            placeholder="example@university.edu"
                            required
                          />
                          <div className="form-group">
                            <label>役割</label>
                            <select
                              value={inviteForm.role}
                              onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as CollaborationRole })}
                              className="role-select"
                            >
                              <option value="viewer">閲覧者（読み取りのみ）</option>
                              <option value="collaborator">共同作業者（編集可能）</option>
                            </select>
                          </div>
                        </div>
                        <div className="form-actions">
                          <Button type="submit" disabled={inviting}>
                            {inviting ? '送信中...' : '招待送信'}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setShowInviteForm(false)}
                            disabled={inviting}
                          >
                            キャンセル
                          </Button>
                        </div>
                      </form>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'collaborators' && isShared && (
            <CollaborationPanel
              examinationId={examinationId}
              userRole={currentUserRole}
              onCollaboratorsChange={() => {
                // 親コンポーネントに変更を通知して画面を更新する
                onSharingToggle(true);
              }}
            />
          )}

          {activeTab === 'activity' && isShared && (
            <ActivityLog examinationId={examinationId} />
          )}
        </div>

        <div className="share-modal__footer">
          <Button variant="ghost" onClick={onClose}>
            閉じる
          </Button>
        </div>
      </div>
    </div>
  );
};

// 活動ログコンポーネント
const ActivityLog: React.FC<{ examinationId: string }> = ({ examinationId }) => {
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadActivities = async () => {
      try {
        const data = await collaborationAPI.getActivityLog(examinationId);
        setActivities(data);
      } catch (error) {
        console.error('活動ログの読み込みに失敗:', error);
      } finally {
        setLoading(false);
      }
    };

    loadActivities();
  }, [examinationId]);

  if (loading) {
    return <div className="activity-log loading">読み込み中...</div>;
  }

  if (activities.length === 0) {
    return <div className="activity-log empty">活動履歴がありません</div>;
  }

  const getActionLabel = (action: string): string => {
    const labels: Record<string, string> = {
      invite_collaborator: '共同作業者を招待',
      accept_invitation: '招待を受諾',
      remove_collaborator: '共同作業者を削除',
      update_examination: 'プロジェクトを更新',
      add_product: '評価対象を追加',
      update_product: '評価対象を更新',
      delete_product: '評価対象を削除',
      add_criterion: '評価観点を追加',
      update_criterion: '評価観点を更新',
      delete_criterion: '評価観点を削除',
      update_evaluation: '評価を更新',
      set_grades: '等級を設定',
    };
    return labels[action] || action;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="activity-log">
      <div className="activity-list">
        {activities.map((activity) => (
          <div key={activity.id} className="activity-item">
            <div className="activity-item__icon">•</div>
            <div className="activity-item__content">
              <div className="activity-item__text">
                <strong>{activity.userFullName}</strong>が
                <span className="activity-action">{getActionLabel(activity.action)}</span>
                しました
              </div>
              <div className="activity-item__time">
                {formatDate(activity.createdAt)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
